if (!window.__aftLandingBootstrapped) {
  window.__aftLandingBootstrapped = true;

  const numberPattern = /^(.*?)(\d+(?:\.\d+)?)(.*)$/s;
  const animatedNumbers = new WeakSet();
  const autoScrollCancelKeys = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar']);
  const autoScrollState = {
    timerId: null,
    cancelled: false,
    triggered: false,
    running: false,
    startScrollY: 0,
  };

  const createSwiper = (scope) => {
    if (typeof Swiper === 'undefined') {
      return;
    }

    const swipers = scope.querySelectorAll('.swiper');

    swipers.forEach((swiperElement) => {
      if (swiperElement.dataset.aftSwiperReady === 'true') {
        return;
      }

      swiperElement.dataset.aftSwiperReady = 'true';

      new Swiper(swiperElement, {
        direction: 'horizontal',
        loop: true,
        grabCursor: true,
        allowTouchMove: true,
        simulateTouch: true,
        touchStartPreventDefault: false,
        touchReleaseOnEdges: true,
        threshold: 5,
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
        },
        pagination: {
          el: swiperElement.querySelector('.swiper-pagination'),
          clickable: true,
        },
        navigation: {
          nextEl: swiperElement.querySelector('.swiper-button-next'),
          prevEl: swiperElement.querySelector('.swiper-button-prev'),
        },
        scrollbar: {
          el: swiperElement.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
      });
    });
  };

  const primeEvidenceLines = (block) => {
    const paths = block.querySelectorAll('svg path');

    paths.forEach((path) => {
      const length = path.getTotalLength();

      path.style.transition = 'none';
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;
    });
  };

  const animateEvidenceLines = (block) => {
    const paths = block.querySelectorAll('svg path');

    paths.forEach((path) => {
      const length = path.getTotalLength();

      path.style.transition = 'none';
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          path.style.transition = 'stroke-dashoffset 1.6s ease';
          path.style.strokeDashoffset = '0';
        });
      });
    });
  };

  const initEvidenceAnimations = (scope) => {
    const evidenceBlocks = scope.querySelectorAll('.evidence__shrimp');

    if (!evidenceBlocks.length) {
      return;
    }

    document.documentElement.classList.add('has-scroll-animations');

    evidenceBlocks.forEach((block) => {
      if (block.dataset.aftEvidencePrimed === 'true') {
        return;
      }

      block.dataset.aftEvidencePrimed = 'true';
      primeEvidenceLines(block);
    });

    if (!('IntersectionObserver' in window)) {
      evidenceBlocks.forEach((block) => {
        animateEvidenceLines(block);
        block.classList.add('is-visible');
      });

      return;
    }

    const evidenceObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        animateEvidenceLines(entry.target);
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.35,
      rootMargin: '0px 0px -10% 0px',
    });

    evidenceBlocks.forEach((block) => {
      if (block.dataset.aftEvidenceObserved === 'true') {
        return;
      }

      block.dataset.aftEvidenceObserved = 'true';
      evidenceObserver.observe(block);
    });
  };

  const getAnimatableTextNode = (element) => {
    const textNode = Array.from(element.childNodes).find((node) => {
      return node.nodeType === Node.TEXT_NODE && /\d/.test(node.textContent);
    });

    if (!textNode) {
      return null;
    }

    const match = textNode.textContent.match(numberPattern);

    if (!match) {
      return null;
    }

    const rawNumber = match[2];
    const parsedNumber = Number.parseFloat(rawNumber);

    if (Number.isNaN(parsedNumber)) {
      return null;
    }

    return {
      node: textNode,
      prefix: match[1],
      value: parsedNumber,
      suffix: match[3],
      decimals: rawNumber.includes('.') ? rawNumber.split('.')[1].length : 0,
    };
  };

  const formatAnimatedNumber = (value, decimals) => {
    if (decimals > 0) {
      return value.toFixed(decimals);
    }

    return `${Math.round(value)}`;
  };

  const animateNumber = (element) => {
    if (animatedNumbers.has(element)) {
      return;
    }

    const config = getAnimatableTextNode(element);

    if (!config) {
      return;
    }

    animatedNumbers.add(element);

    const duration = 1400;
    const start = performance.now();

    const step = (timestamp) => {
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - ((1 - progress) ** 3);
      const currentValue = config.value * eased;

      config.node.textContent = `${config.prefix}${formatAnimatedNumber(currentValue, config.decimals)}${config.suffix}`;

      if (progress < 1) {
        requestAnimationFrame(step);
        return;
      }

      config.node.textContent = `${config.prefix}${formatAnimatedNumber(config.value, config.decimals)}${config.suffix}`;
    };

    config.node.textContent = `${config.prefix}${formatAnimatedNumber(0, config.decimals)}${config.suffix}`;
    requestAnimationFrame(step);
  };

  const initNumberAnimations = (scope) => {
    const numberTargets = scope.querySelectorAll([
      '.works__first-content--block p',
      '.works__second-content--description span',
      '.evidence__shrimp-block--content p',
      '.statistic__statistic-numbers p',
      '.taste__text p span',
    ].join(','));

    if (!numberTargets.length) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      numberTargets.forEach((element) => {
        animateNumber(element);
      });

      return;
    }

    const numberObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        animateNumber(entry.target);
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.45,
      rootMargin: '0px 0px -10% 0px',
    });

    numberTargets.forEach((element) => {
      if (element.dataset.aftNumberObserved === 'true') {
        return;
      }

      element.dataset.aftNumberObserved = 'true';
      numberObserver.observe(element);
    });
  };

  const cleanupAutoScrollListeners = () => {
    window.removeEventListener('wheel', cancelAutoScrollOnUserAction);
    window.removeEventListener('touchmove', cancelAutoScrollOnUserAction);
    window.removeEventListener('keydown', cancelAutoScrollOnUserAction);
    window.removeEventListener('scroll', cancelAutoScrollOnUserScroll);
  };

  const cancelAutoScroll = () => {
    if (autoScrollState.cancelled || autoScrollState.triggered) {
      cleanupAutoScrollListeners();
      return;
    }

    autoScrollState.cancelled = true;

    if (autoScrollState.timerId !== null) {
      window.clearTimeout(autoScrollState.timerId);
      autoScrollState.timerId = null;
    }

    cleanupAutoScrollListeners();
  };

  function cancelAutoScrollOnUserAction(event) {
    if (event.type === 'keydown' && !autoScrollCancelKeys.has(event.key)) {
      return;
    }

    cancelAutoScroll();
  }

  function cancelAutoScrollOnUserScroll() {
    if (autoScrollState.running) {
      return;
    }

    if (Math.abs(window.scrollY - autoScrollState.startScrollY) > 4) {
      cancelAutoScroll();
    }
  }

  const getAutoScrollTarget = () => {
    return document.querySelector('.freezing .swiper')
      || document.querySelector('.freezing')
      || document.querySelector('.shopify-section .swiper');
  };

  const getCenteredScrollTop = (targetElement) => {
    if (!targetElement) {
      return null;
    }

    const elementRect = targetElement.getBoundingClientRect();
    const absoluteTop = window.scrollY + elementRect.top;
    const centeredTop = absoluteTop - ((window.innerHeight - elementRect.height) / 2);
    const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    return Math.min(maxScrollTop, Math.max(0, Math.round(centeredTop)));
  };

  // const initAutoScroll = () => {
  //   if (window.__aftLandingAutoScrollReady) {
  //     return;
  //   }

  //   window.__aftLandingAutoScrollReady = true;

  //   if (window.location.hash || window.scrollY > 4) {
  //     return;
  //   }

  //   if (window.Shopify && Shopify.designMode) {
  //     return;
  //   }

  //   const targetElement = getAutoScrollTarget();

  //   if (!targetElement) {
  //     return;
  //   }

  //   autoScrollState.startScrollY = window.scrollY;

  //   window.addEventListener('wheel', cancelAutoScrollOnUserAction, { passive: true });
  //   window.addEventListener('touchmove', cancelAutoScrollOnUserAction, { passive: true });
  //   window.addEventListener('keydown', cancelAutoScrollOnUserAction);
  //   window.addEventListener('scroll', cancelAutoScrollOnUserScroll, { passive: true });

  //   autoScrollState.timerId = window.setTimeout(() => {
  //     if (autoScrollState.cancelled || autoScrollState.triggered) {
  //       return;
  //     }

  //     const currentTargetElement = getAutoScrollTarget();

  //     if (!currentTargetElement) {
  //       cancelAutoScroll();
  //       return;
  //     }

  //     autoScrollState.triggered = true;
  //     autoScrollState.running = true;
  //     cleanupAutoScrollListeners();

  //     const targetScrollTop = getCenteredScrollTop(currentTargetElement);

  //     if (targetScrollTop === null) {
  //       cancelAutoScroll();
  //       return;
  //     }

  //     window.scrollTo({
  //       top: targetScrollTop,
  //       behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  //     });

  //     window.setTimeout(() => {
  //       autoScrollState.running = false;
  //     }, 1000);
  //   }, 3000);
  // };

  const initAftLanding = (scope = document) => {
    createSwiper(scope);
    initEvidenceAnimations(scope);
    initNumberAnimations(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAftLanding(document);
    });
  } else {
    initAftLanding(document);
  }

  if (document.readyState === 'complete') {
    // initAutoScroll();
  } else {
    // window.addEventListener('load', initAutoScroll, { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAftLanding(event.target);
  });
}
